// RunHistoryList
//
// Lists previous runs for a codebase.
// Users can continue a run or select two runs for comparison.

import React, {
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import runService from "../../services/runService";
import { ChatContext } from "../editor/ChatContext";
import { getModelDisplayName } from "../../utils/util.jsx";

const formatPercentageMetric = (
  value,
  decimals = 1
) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return `${(numericValue * 100).toFixed(decimals)}%`;
};

const resultsSummary = (run) => {
  if (run.status === "pending") {
    return "Not run yet";
  }

  if (run.status === "failed") {
    return "Last run failed";
  }

  const parts = [];

  if (run.epochs != null) {
    parts.push(`${run.epochs} epochs`);
  }

  const trainAccuracy = formatPercentageMetric(
    run.train_accuracy
  );

  if (trainAccuracy) {
    parts.push(`train acc ${trainAccuracy}`);
  }

  const validationAccuracy =
    formatPercentageMetric(run.val_accuracy);

  if (validationAccuracy) {
    parts.push(`val acc ${validationAccuracy}`);
  }

  const f1Score = formatPercentageMetric(
    run.f1_score
  );

  if (f1Score) {
    parts.push(`F1 ${f1Score}`);
  }

  return parts.length > 0
    ? parts.join(" · ")
    : "Success";
};

const RunHistoryList = ({ codebaseId }) => {
  const navigate = useNavigate();

  const { availableModels } = useContext(
    ChatContext
  );

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRuns = async () => {
      const userId =
        sessionStorage.getItem("userId");

      const sessionToken =
        sessionStorage.getItem("session_token");

      if (!userId || !sessionToken || !codebaseId) {
        setLoading(false);
        return;
      }

      try {
        setError(null);

        const data =
          await runService.listRunsForCodebase(
            userId,
            sessionToken,
            codebaseId
          );

        setRuns(data || []);
      } catch (requestError) {
        console.error(
          "Error fetching run history:",
          requestError
        );

        setError("Could not load run history.");
      } finally {
        setLoading(false);
      }
    };

    fetchRuns();
  }, [codebaseId]);

  const toggleSelected = (runId) => {
    setSelected((previousSelection) => {
      if (previousSelection.includes(runId)) {
        return previousSelection.filter(
          (selectedId) => selectedId !== runId
        );
      }

      if (previousSelection.length >= 2) {
        return [
          previousSelection[1],
          runId,
        ];
      }

      return [
        ...previousSelection,
        runId,
      ];
    });
  };

  const continueChat = (runId) => {
    navigate(
      `/codebase/edit/${codebaseId}/data-preparation?runId=${runId}`
    );
  };

  const compareSelected = () => {
    if (selected.length !== 2) {
      return;
    }

    navigate(
      `/compare?runA=${selected[0]}&runB=${selected[1]}`
    );
  };

  if (loading) {
    return (
      <p className="text-sm text-[#64748B]">
        Loading run history...
      </p>
    );
  }

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-[#003153]">
            Run History
          </h2>

          <p className="text-xs text-[#64748B] mt-1">
            Select two runs to compare accuracy,
            F1 score, and output.
          </p>
        </div>

        <button
          type="button"
          disabled={selected.length !== 2}
          onClick={compareSelected}
          className="text-xs sm:text-sm font-semibold px-3 py-2 rounded-lg bg-[#0EA5E9] text-white disabled:bg-[#CBD5E1] disabled:cursor-not-allowed hover:bg-[#0284C7] transition"
        >
          Compare Selected ({selected.length}/2)
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">
          {error}
        </p>
      )}

      {runs.length === 0 ? (
        <p className="text-sm text-[#64748B]">
          No runs yet. Chat with the assistant
          or run your code to start one.
        </p>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => (
            <div
              key={run.id}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition ${
                selected.includes(run.id)
                  ? "border-[#0EA5E9] bg-sky-50"
                  : "border-[#E2E8F0] bg-[#F8FAFC]"
              }`}
            >
              <input
                type="checkbox"
                aria-label={`Select run ${run.id}`}
                checked={selected.includes(run.id)}
                onChange={() =>
                  toggleSelected(run.id)
                }
                className="h-4 w-4"
              />

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0F172A] truncate">
                  {getModelDisplayName(
                    availableModels,
                    run.selected_model
                  )}
                </p>

                <p className="text-xs text-[#64748B] mt-0.5">
                  {run.updated_at
                    ? new Date(
                        run.updated_at
                      ).toLocaleString()
                    : "Date unavailable"}
                  {" · "}
                  {resultsSummary(run)}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  continueChat(run.id)
                }
                className="text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg bg-[#003153] text-white hover:bg-[#1D4ED8] transition whitespace-nowrap"
              >
                Continue Chat
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RunHistoryList;